import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiPlus,
  FiSearch,
  FiGrid,
  FiList,
  FiRotateCcw,
  FiInbox,
  FiEdit2,
  FiTrash2,
  FiMail,
  FiPhone,
  FiMapPin,
  FiCalendar,
  FiBriefcase,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import Notification, { showToast } from "../../../components/common/notification/Notification";
import SaaSLoadingSkeleton from "../../../components/common/saas/SaaSLoadingSkeleton";
import usePersistentState from "../../../hooks/usePersistentState";
import { getErrorText } from "../services/partnerNetworkService";
import { useRecordList } from "../hooks/useNetworkRecords";
import { NETWORK_PAGE_SIZE } from "../constants/networkEndpoints";
import { fullName, formatDate, isActiveRecord, locationOf } from "../utils/people";
import PersonAvatar from "./PersonAvatar";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import "../../property/PropertiesPage.css";
import "../../property/components/PropertyPageHeader.css";
import "../../property/components/PropertyFilterToolbar.css";
import "../partnerNetwork.css";

/**
 * List page shared by Partners and Clients: header with count, search, grid / list toggle, skeleton, empty and
 * error states, and delete with a small confirmation. `config` carries what differs between the two lists.
 */
const PeopleListPage = ({ config, load, remove, permissions }) => {
  const { noun, plural, icon: Icon, home, description, searchLabel, emptyHint, storageKey, canEditRecords } = config;
  const navigate = useNavigate();
  const { items, loading, error, refetch } = useRecordList(load, `Unable to load ${plural.toLowerCase()}. Please check your network connection.`);

  const [currentPage, setCurrentPage] = usePersistentState(`list:${storageKey}:page`, 1);
  const [searchTerm, setSearchTerm] = usePersistentState(`list:${storageKey}:search`, "");
  const [viewMode, setViewMode] = usePersistentState(`list:${storageKey}:view`, "grid");

  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) =>
      [fullName(p), p.mobile_number, p.email_address, p.city_name, p.state_name].some((v) => v && String(v).toLowerCase().includes(q))
    );
  }, [items, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / NETWORK_PAGE_SIZE));
  const paged = filtered.slice((currentPage - 1) * NETWORK_PAGE_SIZE, currentPage * NETWORK_PAGE_SIZE);
  const hasFilters = Boolean(searchTerm.trim());
  const createPath = `${home}/new`;

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages, setCurrentPage]);

  const resetFilters = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  const closeDialog = useCallback(() => setToDelete(null), []);

  const handleConfirmDelete = async () => {
    try {
      setDeleting(true);
      await remove(toDelete.id);
      showToast(`${noun} deleted successfully`, { type: "success" });
      setToDelete(null);
      refetch();
    } catch (err) {
      showToast(getErrorText(err, `Unable to delete ${noun.toLowerCase()}`), { type: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const renderActions = (person) => {
    const name = fullName(person);
    const active = isActiveRecord(person);
    return (
      <div className="pn-actions">
        <Link to={`${home}/${person.id}`} state={{ record: person }} className="btn btn-sm btn-outline-secondary">
          View
        </Link>
        {canEditRecords && permissions.canEdit && active && (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            title="Edit"
            aria-label={`Edit ${name}`}
            onClick={() => navigate(`${home}/${person.id}/edit`, { state: { record: person } })}
          >
            <FiEdit2 size={14} />
          </button>
        )}
        {permissions.canDelete && active && (
          <button type="button" className="btn btn-sm btn-outline-danger" title="Delete" aria-label={`Delete ${name}`} onClick={() => setToDelete(person)}>
            <FiTrash2 size={14} />
          </button>
        )}
      </div>
    );
  };

  const renderDetails = (person) => (
    <div className="pn-details">
      {person.mobile_number && (
        <span>
          <FiPhone aria-hidden="true" /> {person.mobile_number}
        </span>
      )}
      {person.email_address && (
        <span className="pn-details__email" title={person.email_address}>
          <FiMail aria-hidden="true" /> {person.email_address}
        </span>
      )}
      {[person.specialization, person.company].filter(Boolean).length > 0 && (
        <span title="Specialization / company">
          <FiBriefcase aria-hidden="true" /> {[person.specialization, person.company].filter(Boolean).join(" · ")}
        </span>
      )}
      {[person.client_type, person.budget].filter(Boolean).length > 0 && (
        <span title="Client type / budget">
          <FiBriefcase aria-hidden="true" /> {[person.client_type, person.budget].filter(Boolean).join(" · ")}
        </span>
      )}
      {locationOf(person) && (
        <span>
          <FiMapPin aria-hidden="true" /> {locationOf(person)}
        </span>
      )}
      {person.created_on && (
        <span>
          <FiCalendar aria-hidden="true" /> Added {formatDate(person.created_on)}
        </span>
      )}
    </div>
  );

  const addLink = permissions.canAdd && (
    <Link to={createPath} className="property-page-header__create-btn">
      <FiPlus size={18} />
      <span>Add {noun}</span>
    </Link>
  );

  return (
    <div className="properties-module-page">
      <Notification />

      <div className="property-page-header">
        <div className="property-page-header__content">
          <div className="property-page-header__badge">
            <Icon className="property-page-header__badge-icon" />
            <span>Partner Network</span>
            {!loading && !error && <span className="property-page-header__count-tag">{items.length} Listed</span>}
          </div>
          <h1 className="property-page-header__title">{plural}</h1>
          <p className="property-page-header__description">{description}</p>
        </div>
        {addLink && <div className="property-page-header__actions">{addLink}</div>}
      </div>

      <div className="property-filter-toolbar">
        <div className="property-filter-toolbar__controls">
          <form className="property-filter-toolbar__search" role="search" onSubmit={(e) => e.preventDefault()}>
            <FiSearch className="property-filter-toolbar__search-icon" />
            <input
              type="text"
              aria-label={searchLabel}
              placeholder="Search name, mobile, email, city..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="property-filter-toolbar__search-input"
            />
            {searchTerm && (
              <button type="button" className="property-filter-toolbar__clear-btn" aria-label="Clear search" onClick={() => setSearchTerm("")}>
                ×
              </button>
            )}
          </form>

          <div className="property-filter-toolbar__selects">
            {hasFilters && (
              <button type="button" className="property-filter-reset-btn" onClick={resetFilters} title="Reset Filters">
                <FiRotateCcw size={14} />
                <span>Reset</span>
              </button>
            )}
          </div>

          <div className="property-view-toggle">
            <button type="button" aria-label="Grid view" className={`property-view-toggle__btn ${viewMode === "grid" ? "property-view-toggle__btn--active" : ""}`} onClick={() => setViewMode("grid")} title="Grid View">
              <FiGrid size={16} />
            </button>
            <button type="button" aria-label="List view" className={`property-view-toggle__btn ${viewMode === "table" ? "property-view-toggle__btn--active" : ""}`} onClick={() => setViewMode("table")} title="List View">
              <FiList size={16} />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div aria-busy="true" aria-label={`Loading ${plural.toLowerCase()}`}>
          <SaaSLoadingSkeleton type={viewMode === "grid" ? "card" : "list"} count={6} />
        </div>
      ) : error ? (
        <div className="alert alert-danger d-flex align-items-center justify-content-between p-3 rounded-3 my-4" role="alert">
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={refetch}>
            Retry
          </button>
        </div>
      ) : paged.length === 0 ? (
        <div className="properties-empty-state">
          <div className="properties-empty-state__icon">
            <FiInbox size={48} />
          </div>
          <h3 className="properties-empty-state__title">{hasFilters ? `No Matching ${plural}` : `You haven't added any ${plural.toLowerCase()} yet`}</h3>
          <p className="properties-empty-state__text">{hasFilters ? "Try adjusting your search terms." : emptyHint}</p>
          {hasFilters ? (
            <button type="button" className="btn btn-outline-success mt-2" onClick={resetFilters}>
              Clear Search
            </button>
          ) : (
            permissions.canAdd && (
              <Link to={createPath} className="btn btn-primary mt-2">
                Add {noun}
              </Link>
            )
          )}
        </div>
      ) : viewMode === "grid" ? (
        <div className="properties-grid">
          {paged.map((person) => (
            <div key={person.id} className="pn-card">
              <div className="pn-card__head">
                <PersonAvatar person={person} />
                <h5 className="pn-card__name" title={fullName(person)}>
                  <Link to={`${home}/${person.id}`} state={{ record: person }}>
                    {fullName(person)}
                  </Link>
                </h5>
              </div>
              {renderDetails(person)}
              {renderActions(person)}
            </div>
          ))}
        </div>
      ) : (
        <ul className="pn-rows">
          {paged.map((person) => (
            <li key={person.id} className="pn-row">
              <PersonAvatar person={person} />
              <div className="pn-row__main">
                <Link to={`${home}/${person.id}`} state={{ record: person }} className="pn-row__name">
                  {fullName(person)}
                </Link>
                {renderDetails(person)}
              </div>
              {renderActions(person)}
            </li>
          ))}
        </ul>
      )}

      {totalPages > 1 && !loading && !error && (
        <div className="properties-pagination-bar">
          <button type="button" className="properties-page-nav-btn" disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>
            <FiChevronLeft size={16} />
            <span>Previous</span>
          </button>
          <span className="properties-page-counter">
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </span>
          <button type="button" className="properties-page-nav-btn" disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>
            <span>Next</span>
            <FiChevronRight size={16} />
          </button>
        </div>
      )}

      {toDelete && (
        <DeleteConfirmDialog
          title={`Delete ${noun.toLowerCase()}?`}
          message={`"${fullName(toDelete)}" will be removed from your ${plural.toLowerCase()}.`}
          busy={deleting}
          onCancel={closeDialog}
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  );
};

export default PeopleListPage;
